<template>
  <vs-popup 
    class="eval-criteria-popup" 
    title="Talent Evaluation & Compensation Fitment" 
    :active.sync="isActive"
  >
    <div class="comp-box" style="padding: 0; overflow: hidden; min-width: 800px;">
      
      <!-- Warning if Role is not configured -->
      <div v-if="userId && totalWeight !== 100 && !isLoading" class="bg-red-50 text-red-600 p-3 text-sm font-semibold text-center border-b border-red-100">
          ⚠️ Role metrics are not configured. Please edit the role and assign weights totaling 100% before evaluating candidates.
      </div>

      <!-- Header -->
      <div class="flex justify-between items-center p-6 border-b border-gray-100 bg-white">
        <div class="text-left">
          <h5 class="text-lg font-bold text-gray-900 mb-1 text-left">Evaluation Criteria & Fitment</h5>
          <p v-if="userId" class="text-xs text-gray-500 m-0 text-left">Score the candidate (1-5). The system calculates Job Grade and target Salary based on the Role's requirements.</p>
          <p v-else class="text-xs text-gray-500 m-0 text-left">Weight × score (1-5) = weighted score. Rename, describe, reweight or remove any criterion.</p>
        </div>
        <button v-if="!userId" @click="addFactor" class="comp-btn-outline flex-shrink-0" style="background: white;">+ Add criterion</button>
      </div>

      <!-- Table List -->
      <div class="factor-list w-full overflow-x-auto bg-white">
        <table class="w-full text-left border-collapse min-w-[800px]">
          <thead class="bg-[#f8fafc] border-b border-gray-200">
            <tr>
              <th class="py-3 px-6 text-[11px] font-bold text-gray-500 uppercase tracking-wider text-left w-2/5">Criteria & Description</th>
              <th class="py-3 pr-6 text-[11px] font-bold text-gray-500 uppercase tracking-wider text-left w-1/4">Role Weight</th>
              <th class="py-3 pr-6 text-[11px] font-bold text-gray-500 uppercase tracking-wider text-center">Test Score (1-5)</th>
              <th class="py-3 pr-6 text-[11px] font-bold text-gray-500 uppercase tracking-wider text-center">Weighted Result</th>
              <th v-if="!userId" class="py-3 px-4"></th>
            </tr>
          </thead>
          <tbody>
            <!-- 1. STRICT LOADING STATE -->
            <tr v-if="isLoading">
                <td colspan="5" class="py-8 text-center text-gray-400 italic font-medium">Loading metrics from database...</td>
            </tr>
            
            <!-- 2. STRICT DATA STATE (Using v-if="!isLoading" instead of v-else to prevent DOM crashes) -->
            <template v-if="!isLoading">
              <tr v-for="(factor, index) in factors" :key="index" v-show="factor" class="border-b border-gray-50 hover:bg-[#fafbfc] transition-colors">
                
                <!-- 1. Criteria Name & Description -->
                <td class="py-4 px-6 align-top">
                  <div class="flex flex-col gap-1">
                    <template v-if="userId">
                        <div class="font-bold text-[14px] text-gray-900">{{ factor.name }}</div>
                        <div class="text-[12px] text-gray-500 italic leading-snug">{{ factor.desc }}</div>
                    </template>
                    <template v-if="!userId">
                        <input type="text" v-model="factor.name" class="factor-input-title" placeholder="Criteria Name" />
                        <input type="text" v-model="factor.desc" class="factor-input-desc" placeholder="Describe what is being measured..." />
                    </template>
                  </div>
                </td>
                
                <!-- 2. Weight Allocation -->
                <td class="py-4 pr-6 align-top pt-6">
                  <template v-if="userId">
                      <div class="font-bold text-[15px] text-gray-700 pl-1">
                          {{ factor.weight }}%
                      </div>
                  </template>
                  <template v-if="!userId">
                      <div class="flex items-center gap-2">
                          <button @click="decrementWeight(factor)" class="weight-btn" title="Decrease">
                              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                          </button>
                          <input type="range" v-model.number="factor.weight" min="0" max="100" class="factor-slider w-full" />
                          <button @click="incrementWeight(factor)" class="weight-btn" title="Increase">
                              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                          </button>
                          <div class="weight-input-wrapper ml-2">
                              <input type="number" v-model.number="factor.weight" min="0" max="100" class="weight-input-field" />
                              <span class="weight-input-suffix">%</span>
                          </div>
                      </div>
                  </template>
                </td>

                <!-- 3. Test Score -->
                <td class="py-4 pr-6 align-top pt-4">
                  <div class="flex justify-center gap-1.5">
                    <button v-for="n in 5" :key="n" @click="$set(factor, 'previewScore', n)" :class="['score-circle', factor.previewScore === n ? 'active' : '']">{{ n }}</button>
                  </div>
                </td>

                <!-- 4. Weighted Result -->
                <td class="py-4 pr-6 text-center align-top pt-6">
                  <span class="font-bold text-[15px] text-gray-900">
                    {{ ((factor.weight / 100) * (factor.previewScore || 1)).toFixed(2) }}
                  </span>
                </td>

                <!-- 5. Delete Button -->
                <td v-if="!userId" class="py-4 px-4 text-right align-top pt-6">
                  <button @click="removeFactor(index)" class="factor-del-btn" title="Remove Criteria">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                  </button>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <!-- Footer / Save Actions -->
      <div class="p-6 bg-gray-50 flex justify-between items-center border-t border-gray-200">
        
        <!-- Left Side: Weight Tracker -->
        <div class="flex items-center gap-2">
          <span class="text-sm font-semibold text-gray-600">Total Weight:</span>
          <span :class="['px-3 py-1 rounded-full text-xs font-bold border', totalWeight === 100 ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200']">
            {{ totalWeight }}%
          </span>
          <span v-if="totalWeight !== 100 && !userId" class="text-xs text-red-500 ml-2">Must equal exactly 100% to save.</span>
        </div>
        
        <!-- Right Side: Intelligence Engine & Save -->
        <div class="flex items-center gap-4">
          
          <!-- INTELLIGENCE BAR (Score -> Grade -> Salary) -->
          <div class="flex items-center gap-4 bg-white pl-4 pr-1 py-1 border border-gray-200 rounded-lg shadow-sm">
            
            <!-- Final Score -->
            <div class="flex flex-col border-r border-gray-100 pr-4 py-1">
                <span class="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">Final Score</span>
                <div class="flex items-baseline">
                    <span class="text-[18px] font-black text-gray-900 leading-none">{{ finalCalculatedScore }}</span>
                    <span class="text-[12px] font-bold text-gray-400 ml-1 leading-none">/ 5.00</span>
                </div>
            </div>

            <!-- Job Grade -->
            <div v-if="calculatedGrade" class="flex flex-col border-r border-gray-100 pr-4 py-1">
                <span class="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">Job Grade</span>
                <div class="flex items-baseline gap-1.5">
                    <span class="text-[15px] font-black text-[#F2C244] leading-none">{{ calculatedGrade.gradeLabel || calculatedGrade.grade_label || calculatedGrade.id || 'N/A' }}</span>
                    <vx-tooltip v-if="calculatedGrade.meaning || calculatedGrade.description" :text="calculatedGrade.meaning || calculatedGrade.description" position="top">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M12 16v-4"></path><path d="M12 8h.01"></path></svg>
                    </vx-tooltip>
                </div>
            </div>

            <!-- Recommended Salary -->
            <div class="flex flex-col pr-2 py-1">
                <span class="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">Target Salary</span>
                <div class="flex items-baseline">
                    <span v-if="calculatedSalary" class="text-[18px] font-black text-green-600 leading-none">€{{ calculatedSalary }}</span>
                    <span v-else class="text-[11px] font-bold text-gray-400 italic leading-none">Band not configured</span>
                </div>
            </div>

          </div>

          <!-- Save Button -->
          <button @click="saveFactors" class="comp-btn-primary" :disabled="totalWeight !== 100 || isSavingFactors">
            {{ isSavingFactors ? 'Saving...' : 'Save Evaluation' }}
          </button>
        </div>

      </div>
    </div>
  </vs-popup>
</template>

<script>
export default {
  name: "EvaluationCriteriaModal",
  data() {
    return {
      isActive: false,
      isLoading: false,
      orgId: null,
      roleId: null,
      userId: null,
      isSavingFactors: false,
      factors: [],
      onSaveCallback: null,
      
      roleMinSalary: null,
      roleMaxSalary: null,
      jobGrades: []
    };
  },
  computed: {
    totalWeight() {
      if (!this.factors || !Array.isArray(this.factors)) return 0;
      let sum = 0;
      for (let i = 0; i < this.factors.length; i++) {
        const f = this.factors[i];
        if (f && typeof f === 'object') sum += (Number(f.weight) || 0);
      }
      return sum;
    },
    finalCalculatedScore() {
      let score = 0;
      this.factors.forEach(f => {
        score += (Number(f.weight) / 100) * (Number(f.previewScore) || 1);
      });
      return score.toFixed(2);
    },
    calculatedGrade() {
      if (!this.jobGrades || this.jobGrades.length === 0) return null;
      const score = parseFloat(this.finalCalculatedScore);
      
      const sorted = [...this.jobGrades].sort((a, b) => {
        const minA = parseFloat(a.minScore || a.min_score || 0);
        const minB = parseFloat(b.minScore || b.min_score || 0);
        return minB - minA;
      });
      
      const grade = sorted.find(g => {
        const min = parseFloat(g.minScore || g.min_score || 0);
        return score >= min;
      });
      
      return grade || sorted[sorted.length - 1];
    },
    calculatedSalary() {
      if (!this.roleMinSalary || !this.roleMaxSalary || !this.calculatedGrade) return null;
      
      const min = parseFloat(this.roleMinSalary);
      const max = parseFloat(this.roleMaxSalary);
      const position = parseFloat(this.calculatedGrade.position || this.calculatedGrade.band_position || 50) / 100;
      
      const salary = min + ((max - min) * position);
      return salary.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    }
  },
  methods: {
    openModal(organizationId, roleId, userId, callback = null) {
      this.orgId = organizationId;
      this.roleId = roleId;
      this.userId = userId;
      this.onSaveCallback = callback;
      this.isActive = true;
      this.fetchEvaluationData();
    },
    addFactor() {
      this.factors.push({ name: '', weight: 0, desc: '', previewScore: 3 });
    },
    removeFactor(index) {
      this.factors.splice(index, 1);
    },
    incrementWeight(factor) {
      let current = Number(factor.weight) || 0;
      if (current < 100) this.$set(factor, 'weight', current + 1);
    },
    decrementWeight(factor) {
      let current = Number(factor.weight) || 0;
      if (current > 0) this.$set(factor, 'weight', current - 1);
    },
    async fetchEvaluationData() {
      if (!this.orgId) return;
      this.isLoading = true;
      try {
        const token = localStorage.getItem('accessToken') || localStorage.getItem('feathers-jwt');
        const apiUrl = process.env.VUE_APP_API_URL;
        const headers = { 'Authorization': `Bearer ${token}` };

        // 1. Fetch Global Job Grades
        try {
            const gradeRes = await fetch(`${apiUrl}/api/v2/organization/${this.orgId}/job-grades`, { headers });
            if (gradeRes.ok) {
                const json = await gradeRes.json();
                this.jobGrades = json.data || [];
            }
        } catch (e) {}

        // 2. Fetch Role Specific Config (Weights & Salary Bands)
        let roleCriteriaTemplate = [];
        if (this.roleId) {
            try {
                const metaRes = await fetch(`${apiUrl}/api/v2/roles/${this.roleId}/metadata`, { headers });
                if (metaRes.ok) {
                    const json = await metaRes.json();
                    if (json.data) {
                        this.roleMinSalary = json.data.min_salary;
                        this.roleMaxSalary = json.data.max_salary;
                        roleCriteriaTemplate = json.data.evaluation_metrics || [];
                    }
                }
            } catch (e) {}
        }

        // 3. Fetch Base Org Criteria (Fallback Template)
        let baseCriteriaTemplate = [];
        if (roleCriteriaTemplate.length === 0) {
            try {
                const evalRes = await fetch(`${apiUrl}/api/v2/organization/${this.orgId}/evaluation-factors`, { headers });
                if (evalRes.ok) {
                    const json = await evalRes.json();
                    baseCriteriaTemplate = (json.data || []).filter(f => f && typeof f === 'object');
                }
            } catch (e) {}
        }

        // 4. Fetch the Candidate's existing saved scores
        let userSavedScores = [];
        if (this.userId && this.roleId) {
            try {
                const userRes = await fetch(`${apiUrl}/api/v2/user-evaluations/${this.orgId}/${this.roleId}/${this.userId}`, { headers });
                if (userRes.ok) {
                    const userJson = await userRes.json();
                    if (userJson.data && userJson.data.criteria_data) {
                        userSavedScores = userJson.data.criteria_data;
                    }
                }
            } catch (e) {}
        }

        // --- MERGE LOGIC (CRITICAL FIX) ---
        // We ALWAYS use the Role Template (or Org Base) as the source of truth for Names & Weights.
        // We only use the user's saved data to restore their 1-5 circle scores!
        
        let masterTemplate = [];
        if (roleCriteriaTemplate.length > 0) {
            masterTemplate = JSON.parse(JSON.stringify(roleCriteriaTemplate));
        } else if (baseCriteriaTemplate.length > 0) {
            masterTemplate = JSON.parse(JSON.stringify(baseCriteriaTemplate));
            masterTemplate.forEach(t => t.weight = 0); // Org base has no weights
        }

        // Now map the user's 1-5 scores onto the master template
        this.factors = masterTemplate.map(templateItem => {
            let restoredScore = 3; // Default score
            
            if (userSavedScores && userSavedScores.length > 0) {
                // Find if the user was previously graded on this exact metric name
                const matchingSavedItem = userSavedScores.find(saved => saved.name === templateItem.name);
                if (matchingSavedItem && matchingSavedItem.previewScore) {
                    restoredScore = matchingSavedItem.previewScore;
                }
            }
            
            return {
                ...templateItem,
                previewScore: restoredScore
            };
        });

      } catch (err) { 
        console.error("Fetch failed", err); 
      } finally {
        this.isLoading = false;
      }
    },
    async saveFactors() {
      this.isSavingFactors = true;
      try {
        const token = localStorage.getItem('accessToken') || localStorage.getItem('feathers-jwt');
        const apiUrlPath = process.env.VUE_APP_API_URL;
        
        let apiUrl = '';
        let payload = {};

        // Save as Candidate Evaluation
        if (this.userId && this.roleId) {
            apiUrl = `${apiUrlPath}/api/v2/user-evaluations/${this.orgId}/${this.roleId}/${this.userId}`;
            payload = { 
                criteria: this.factors, 
                total_score: parseFloat(this.finalCalculatedScore),
                total_weighted_score: parseFloat(this.finalCalculatedScore) 
            };
        } else {
            // Fallback save as Org config
            apiUrl = `${apiUrlPath}/api/v2/organization/${this.orgId}/evaluation-factors`;
            payload = { factors: this.factors };
        }

        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        
        if (response.ok) {
          this.$vs.notify({ title: 'Success', text: this.userId ? 'Candidate Evaluation Saved!' : 'Organization Criteria Saved.', color: 'success' });
          
          if (this.userId) {
              document.dispatchEvent(new CustomEvent('update-job-grade-tags', {
                  detail: {
                      userId: this.userId,
                      newScore: parseFloat(this.finalCalculatedScore)
                  }
              }));
          }

          this.isActive = false; 
          setTimeout(() => { document.dispatchEvent(new CustomEvent('request-talentpool-refresh')); }, 300);

        } else throw new Error("Failed to save");
      } catch (err) {
        this.$vs.notify({ title: 'Error', text: 'Could not save data.', color: 'danger' });
      } finally {
        this.isSavingFactors = false;
      }
    }
  }
};
</script>

<style scoped>
/* =======================================================
   OVERRIDE VUESAX POPUP LIMITS TO MAKE IT WIDE & CLEAN
   ======================================================= */
.eval-criteria-popup ::v-deep .vs-popup { 
  width: 95vw !important; 
  max-width: 1100px !important; 
  border-radius: 12px !important;
  transform: translateZ(0); /* Prevents blur backdrop lag */
}

.eval-criteria-popup ::v-deep .vs-popup--content {
  padding: 0 !important;
  margin: 0 !important;
  overflow: hidden !important; 
}

.eval-criteria-popup ::v-deep .vs-popup--header {
  border-bottom: 1px solid #f1f5f9;
  background: #ffffff;
}


/* =======================================================
   INTERNAL UI STYLES
   ======================================================= */
.comp-box { 
  background: #ffffff; 
  width: 100%; 
  display: flex; 
  flex-direction: column; 
  max-height: 85vh; /* Stops the modal from pushing off-screen */
}

.factor-list {
  width: 100%;
  flex: 1; /* Takes up remaining space in the modal */
  overflow-y: auto; /* Forces vertical scrolling INSIDE the table container */
  overflow-x: auto;
  padding: 0;
  -webkit-overflow-scrolling: touch; /* GPU smooth scroll */
  transform: translateZ(0); /* Hardware acceleration */
  will-change: transform;
}

/* BONUS FIX: Makes the table header stick to the top when you scroll */
.factor-list thead th {
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: #f8fafc; 
  box-shadow: 0 1px 2px rgba(0,0,0,0.05); /* Separates header from scrolling content */
}

table {
  width: 100%;
  table-layout: auto; 
}

td, th {
  vertical-align: middle !important;
}

/* Buttons */
.comp-btn-primary { background: #F2C244; color: #0f172a; border: none; padding: 10px 24px; border-radius: 8px; font-size: 14px; font-weight: 700; cursor: pointer; transition: all 0.2s; }
.comp-btn-primary:hover:not(:disabled) { background: #eab308; }
.comp-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; background: #e2e8f0; color: #94a3b8; }
.comp-btn-outline { background: transparent; border: 1px solid #cbd5e1; color: #475569; padding: 8px 16px; border-radius: 8px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.2s; }
.comp-btn-outline:hover { background: #f8fafc; }

/* Text Inputs */
.factor-input-title { width: 100%; min-width: 250px; border: 1px solid transparent; background: transparent; font-size: 14px; font-weight: 700; color: #0f172a; padding: 6px 8px; border-radius: 6px; outline: none; transition: all 0.2s; }
.factor-input-title:focus { border-color: #F2C244; background: #ffffff; box-shadow: 0 0 0 2px rgba(242,194,68,0.1); }
.factor-input-title:hover { border-color: #e2e8f0; }

.factor-input-desc { width: 100%; min-width: 250px; border: 1px solid transparent; background: transparent; font-size: 12px; color: #64748b; padding: 6px 8px; border-radius: 6px; outline: none; transition: all 0.2s; }
.factor-input-desc:focus { border-color: #F2C244; background: #ffffff; }
.factor-input-desc:hover { border-color: #e2e8f0; }

/* Interactive Slider & Scoring */
.factor-slider { -webkit-appearance: none; width: 100%; min-width: 120px; height: 6px; border-radius: 6px; background: #e2e8f0; outline: none; margin: 0 8px; }
.factor-slider::-webkit-slider-thumb { -webkit-appearance: none; appearance: none; width: 16px; height: 16px; border-radius: 50%; background: #F2C244; border: 2px solid #0f172a; cursor: pointer; transition: transform 0.1s; }
.factor-slider::-webkit-slider-thumb:hover { transform: scale(1.2); }

.score-circle { width: 32px; height: 32px; border-radius: 50%; border: 1px solid #cbd5e1; background: #ffffff; color: #475569; font-size: 13px; font-weight: 600; display: flex; align-items: center; justify-content: center; cursor: pointer; outline: none; transition: all 0.2s; }
.score-circle:hover { background: #f8fafc; }
.score-circle.active { background: #F2C244; border-color: #0f172a; color: #0f172a; font-weight: 800; border-width: 2px; transform: scale(1.1); box-shadow: 0 2px 5px rgba(242, 194, 68, 0.3); }

/* Buttons & Inputs */
.factor-del-btn { background: transparent; border: 1px solid transparent; color: #cbd5e1; cursor: pointer; padding: 6px; border-radius: 50%; display: flex; align-items: center; justify-content: center; transition: all 0.2s; }
.factor-del-btn:hover { color: #ef4444; border-color: #fecaca; background: #fef2f2; }

.weight-btn { background: #f1f5f9; border: none; color: #64748b; width: 26px; height: 26px; border-radius: 50%; display: flex; align-items: center; justify-content: center; cursor: pointer; flex-shrink: 0; transition: all 0.2s; }
.weight-btn:hover { background: #e2e8f0; color: #0f172a; }

.weight-input-wrapper { display: flex; align-items: center; width: 65px; flex-shrink: 0; border: 1px solid #cbd5e1; border-radius: 6px; background: #ffffff; padding: 0 6px; transition: all 0.2s; }
.weight-input-wrapper:focus-within { border-color: #F2C244; box-shadow: 0 0 0 2px rgba(242,194,68,0.1); }
.weight-input-field { width: 100%; border: none; background: transparent; font-size: 14px; font-weight: 700; color: #0f172a; text-align: right; outline: none; padding: 6px 4px 6px 0; -moz-appearance: textfield; }
.weight-input-field::-webkit-outer-spin-button, .weight-input-field::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
.weight-input-suffix { font-size: 12px; font-weight: 700; color: #64748b; pointer-events: none; }
</style>